import React from 'react';
import { useTranslation } from 'localization';
import Loader from '../../atoms/loader/loader';
import ProgressBarAtom from '../../atoms/ProgressBar/ProgressBarAtom';
import './LoaderMolecule.scss';

export interface LoaderMoleculeProps {
  /**
   * Caption shown beneath the orbital spinner. Safe to change while loading —
   * the live region announces each update (e.g. "Loading spheres…" →
   * "Resolving lineage…"). Omit for a caption-less spinner.
   */
  caption?: string;
  /**
   * Determinate progress, 0–100. When provided, a progress bar renders under the
   * caption and animates to the new value on each update. Omit for an
   * indeterminate (spinner-only) loader.
   */
  progress?: number;
  /**
   * `overlay` (default): full-area dark backdrop that covers its positioned
   * ancestor — for blocking a view while it loads (e.g. ArmillaryView).
   * `inline`: no backdrop/positioning — drops into a centering wrapper.
   */
  variant?: 'overlay' | 'inline';
  /** Spinner diameter in px. Defaults to 300 (overlay) / 140 (inline). */
  spinnerSize?: number;
  /** Optional extra class — e.g. the `loader-molecule--fade-out` exit modifier. */
  className?: string;
}

/**
 * Generic loading indicator: the Center orbital spinner (`Loader` atom) with an
 * optional dynamic caption and an optional determinate progress bar, in either an
 * overlay or inline layout. Replaces the bespoke `ArmillaryLoader`.
 */
const LoaderMolecule: React.FC<LoaderMoleculeProps> = ({
  caption,
  progress,
  variant = 'overlay',
  spinnerSize,
  className = '',
}) => {
  const { t } = useTranslation();
  const hasProgress = typeof progress === 'number' && Number.isFinite(progress);
  const clamped = hasProgress ? Math.max(0, Math.min(100, progress)) : 0;
  const size = spinnerSize ?? (variant === 'overlay' ? 300 : 140);

  return (
    <div
      className={`loader-molecule loader-molecule--${variant} ${className}`.trim()}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <Loader size={size} />

      {caption && <div className="loader-molecule__caption">{caption}</div>}

      {hasProgress && (
        <div className="loader-molecule__progress">
          <ProgressBarAtom value={clamped} showValue={false} ariaLabel={caption || t('common.loading')} />
        </div>
      )}
    </div>
  );
};

export default LoaderMolecule;
